import { useEffect, useState } from 'react'
import { Navigate, NavLink, Outlet, useLocation, useMatch } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api, type MeResponse } from '../../../api/client'
import { resolveTheme } from '../../../lib/theme'
import { ThemeSwitch } from '../../../editorial/ThemeSwitch'
import { deskFooterLink } from '../../../desk/nav'
import { focus } from '../../../campaigns-desk/ui'
import { useAgentSettings } from '../../../api/visitorsAgent'
import { vdMayUse, vdShowsSettings, vdTitleLinks } from '../../../lib/visitorsDeskNav'
import { manualHref } from './manual'

const item = (active: boolean) =>
  `rounded-lg px-2.5 py-[9px] font-semibold text-paper ${focus} ${active ? 'bg-ink-600 ring-1 ring-ink-400' : 'hover:bg-ink-600'}`

/** The Visitors Desk shell: its own full-screen area with a side menu, styled like the other desks
 *  (the same editorial_theme preference). */
export function VisitorsDeskLayout() {
  const { data: me } = useQuery<MeResponse>({ queryKey: ['me'], queryFn: () => api.get<MeResponse>('/api/me'), staleTime: 60_000, retry: false })
  const agent = useAgentSettings(me?.role === 'admin')
  const { search } = useLocation()
  const tabMatch = useMatch('/visitors/desk/:pub/:tab')
  const articleMatch = useMatch('/visitors/desk/:pub/articles/:articleId')
  const issueMatch = useMatch('/visitors/desk/:pub/issues/:issueId')
  const settingsMatch = useMatch('/visitors/desk/settings')
  const helpMatch = useMatch('/visitors/desk/help')
  const summaryMatch = useMatch('/visitors/desk/:pub/summary')
  const [systemDark, setSystemDark] = useState(() => typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-color-scheme: dark)').matches)
  useEffect(() => {
    const mq = window.matchMedia?.('(prefers-color-scheme: dark)')
    if (!mq?.addEventListener) return
    const on = (e: MediaQueryListEvent) => setSystemDark(e.matches)
    mq.addEventListener('change', on)
    return () => mq.removeEventListener('change', on)
  }, [])
  const pref = me?.preferences?.editorial_theme ?? 'light'
  const theme = resolveTheme(pref, systemDark)
  useEffect(() => {
    if (!me) return
    document.documentElement.dataset.theme = theme
    return () => { delete document.documentElement.dataset.theme }
  }, [me, theme])
  if (!me) return null
  if (!vdMayUse(me)) return <Navigate to="/" replace />

  const activePub = tabMatch?.params.pub ?? articleMatch?.params.pub ?? issueMatch?.params.pub
  const tab = articleMatch ? 'articles' : issueMatch ? 'issues' : settingsMatch || summaryMatch || helpMatch ? 'today' : tabMatch?.params.tab ?? 'today'
  const links = vdTitleLinks(me, tab, settingsMatch || helpMatch ? '' : search)
  const back = deskFooterLink(me.role)
  const toEditorial = !back && (me.role === 'editor' || me.role === 'writer')

  return (
    <div data-theme={theme} className="min-h-screen bg-ink-900 text-paper font-ui text-[14px] md:flex">
      <nav aria-label="Visitors Desk" className="flex w-full shrink-0 flex-col gap-6 border-b border-ink-500 bg-ink-700 px-3.5 pb-7 pt-6 md:min-h-screen md:w-[264px] md:border-b-0 md:border-r">
        <div className="flex flex-col gap-0.5 px-2">
          <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-amber-glow">AAMCirc</span>
          <span className="font-serif text-[23px] font-medium tracking-tight">Visitors Desk</span>
        </div>
        <div data-slot="vd-top-links" className="flex flex-col gap-1 empty:hidden">
          {me.role === 'admin' && agent.data != null && (
            <NavLink to={`/visitors/desk/${activePub ?? 'all'}/summary`} className={() => item(!!summaryMatch)}>Summary</NavLink>
          )}
        </div>
        <div aria-label="Titles" role="group" className="flex flex-col gap-1">
          {links.map((l) => (
            <NavLink key={l.pub} to={l.to} className={() => item(!settingsMatch && !helpMatch && activePub === l.pub)}>{l.label}</NavLink>
          ))}
        </div>
        {vdShowsSettings(me.role) && (
          <div className="flex flex-col gap-1">
            <NavLink to="/visitors/desk/settings" className={({ isActive }) => item(isActive)}>Desk settings</NavLink>
          </div>
        )}
        <div className="mt-auto flex flex-col gap-3 border-t border-ink-500 px-2.5 pt-3.5 text-[13px]">
          <span className="text-paper-faint">Signed in as <strong className="break-all font-semibold text-paper">{me.email}</strong></span>
          <ThemeSwitch value={pref} />
          <NavLink to={manualHref()} className={({ isActive }) => `underline underline-offset-2 hover:text-paper ${focus} ${isActive ? 'text-paper' : 'text-amber-glow'}`}>User manual</NavLink>
          {back && <NavLink to={back.to} className={`text-amber-glow underline underline-offset-2 hover:text-paper ${focus}`}>{back.label}</NavLink>}
          {toEditorial && <NavLink to="/editorial" className={`text-amber-glow underline underline-offset-2 hover:text-paper ${focus}`}>Back to Editorial Desk</NavLink>}
        </div>
      </nav>
      <main className="min-w-0 flex-1 px-4 py-6 md:px-8">
        <Outlet />
      </main>
    </div>
  )
}
